import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import SearchInput from '../../components/SearchInput';
import { getLevels, type Level } from '../../api/adminClient';
import { API_ORIGIN } from '../../api/baseUrl';

// User type from the main users table
interface OrgUser {
  id: string;
  name: string;
  email: string;
  level: string;
  slack_id: string;
  manager_id: string | null;
  team_name?: string;
  team_id?: string;
  created_at: string;
  updated_at: string;
}

// Uniform card style for all nodes
const cardStyle = { color: 'text-gray-800', bgColor: 'bg-white border-gray-300' };

function getLevelLabel(level: string, levels?: Level[]): string {
  const dbLevel = levels?.find(l => l.name === level);
  return dbLevel?.display_name || level;
}

interface OrgNode {
  user: OrgUser;
  children: OrgNode[];
}

// Build org tree from flat user list
function buildOrgTree(users: OrgUser[]): OrgNode[] {
  const userMap = new Map<string, OrgNode>();
  const roots: OrgNode[] = [];

  users.forEach(user => {
    userMap.set(user.id, { user, children: [] });
  });

  users.forEach(user => {
    const node = userMap.get(user.id)!;
    if (user.manager_id && userMap.has(user.manager_id)) {
      userMap.get(user.manager_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  // Sort children by name
  const sortChildren = (nodes: OrgNode[]) => {
    nodes.sort((a, b) => a.user.name.localeCompare(b.user.name));
    nodes.forEach(node => sortChildren(node.children));
  };
  sortChildren(roots);

  return roots;
}

// Get all descendant IDs for a user
function getAllDescendantIds(users: OrgUser[], userId: string): Set<string> {
  const ids = new Set<string>();
  const queue = [userId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    users.forEach(u => {
      if (u.manager_id === currentId && !ids.has(u.id)) {
        ids.add(u.id);
        queue.push(u.id);
      }
    });
  }

  return ids;
}

// Get path from root to a specific user
function getPathToUser(users: OrgUser[], targetId: string): string[] {
  const path: string[] = [];
  let currentId: string | null = targetId;

  while (currentId) {
    path.unshift(currentId);
    const user = users.find(u => u.id === currentId);
    currentId = user?.manager_id || null;
  }

  return path;
}

// Find user by name (case-insensitive partial match)
function findUserByName(users: OrgUser[], searchTerm: string): OrgUser | null {
  const term = searchTerm.toLowerCase();
  return users.find(u => u.name.toLowerCase().includes(term)) || null;
}

// ============================================================================
// VERTICAL TREE NODE COMPONENT
// ============================================================================

interface VerticalTreeNodeProps {
  node: OrgNode;
  levels?: Level[];
  isHighlighted: boolean;
  highlightPath: Set<string>;
  expandedIds: Set<string>;
  onToggleExpand: (id: string, siblingIds: string[]) => void;
  onSelect: (user: OrgUser) => void;
  selectedId: string | null;
  searchedUserId: string | null;
  siblingIds: string[];
}

function VerticalTreeNode({
  node,
  levels,
  isHighlighted,
  highlightPath,
  expandedIds,
  onToggleExpand,
  onSelect,
  selectedId,
  searchedUserId,
  siblingIds,
}: VerticalTreeNodeProps) {
  const { user, children } = node;
  const hasChildren = children.length > 0;
  const isSelected = selectedId === user.id;
  const isSearchTarget = searchedUserId === user.id;
  const levelLabel = getLevelLabel(user.level, levels);
  const isExpanded = expandedIds.has(user.id);

  // Filter visible children based on highlight path
  const visibleChildren = searchedUserId
    ? children.filter(child => highlightPath.has(child.user.id) || child.user.id === searchedUserId || (searchedUserId === user.id))
    : children;

  // Get child IDs for sibling collapsing
  const childIds = children.map(c => c.user.id);

  // Handle card click - toggle expand and select
  const handleCardClick = () => {
    onSelect(user);
    if (hasChildren) {
      onToggleExpand(user.id, siblingIds);
    }
  };

  return (
    <div className="flex flex-col items-center">
      {/* User Card */}
      <div
        className={clsx(
          'relative px-4 py-3 rounded-xl border-2 cursor-pointer transition-all min-w-[160px] max-w-[200px] text-center',
          isSearchTarget ? 'ring-4 ring-yellow-400 scale-105' : '',
          isSelected ? 'ring-2 ring-primary-500' : '',
          isHighlighted ? cardStyle.bgColor : 'bg-white border-gray-200 opacity-60',
          'hover:shadow-lg',
          hasChildren && 'hover:scale-105'
        )}
        onClick={handleCardClick}
      >
        <div className={clsx('font-semibold text-sm', isHighlighted ? cardStyle.color : 'text-gray-600')}>
          {user.name}
        </div>
        <div className="text-xs text-gray-500 truncate">{levelLabel}</div>
        {user.team_name && (
          <div className="text-xs text-gray-400 truncate mt-0.5">{user.team_name}</div>
        )}

        {/* Report count badge - shows expand/collapse state */}
        {hasChildren && (
          <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
            <span className={clsx(
              'text-xs px-2 py-0.5 rounded-full transition-colors',
              isExpanded ? 'bg-primary-600 text-white' : 'bg-gray-700 text-white'
            )}>
              {children.length} {isExpanded ? '▲' : '▼'}
            </span>
          </div>
        )}
      </div>

      {/* Connector Line + Children */}
      {hasChildren && isExpanded && visibleChildren.length > 0 && (
        <>
          {/* Vertical line from parent down to horizontal bar */}
          <div className="w-0.5 h-6 bg-gray-300" />

          {/* Children row with gap between siblings */}
          <div className="flex items-start gap-2">
            {visibleChildren.map((child, idx) => (
              <div key={child.user.id} className="flex flex-col items-center relative pt-[2px]">
                {/* Horizontal connector: left half (all except first) */}
                {visibleChildren.length > 1 && idx > 0 && (
                  <div className="absolute top-0 left-0 right-1/2 h-0.5 bg-gray-300" style={{ left: '-4px' }} />
                )}
                {/* Horizontal connector: right half (all except last) */}
                {visibleChildren.length > 1 && idx < visibleChildren.length - 1 && (
                  <div className="absolute top-0 left-1/2 right-0 h-0.5 bg-gray-300" style={{ right: '-4px' }} />
                )}

                {/* Vertical line down to child card */}
                <div className="w-0.5 h-4 bg-gray-300" />

                <VerticalTreeNode
                  node={child}
                  levels={levels}
                  isHighlighted={highlightPath.has(child.user.id) || !searchedUserId}
                  highlightPath={highlightPath}
                  expandedIds={expandedIds}
                  onToggleExpand={onToggleExpand}
                  onSelect={onSelect}
                  selectedId={selectedId}
                  searchedUserId={searchedUserId}
                  siblingIds={childIds}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// READ-ONLY USER DETAIL PANEL
// ============================================================================

interface UserDetailPanelProps {
  user: OrgUser;
  allUsers: OrgUser[];
  levels?: Level[];
  onClose: () => void;
}

function UserDetailPanel({ user, allUsers, levels, onClose }: UserDetailPanelProps) {
  const levelLabel = getLevelLabel(user.level, levels);
  const directReports = allUsers.filter(u => u.manager_id === user.id);
  const manager = allUsers.find(u => u.id === user.manager_id);

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center text-2xl font-bold text-gray-500">
            {user.name.charAt(0)}
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{user.name}</h2>
            <p className="text-gray-500 text-sm">{user.email}</p>
            <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium border bg-gray-100 border-gray-300 text-gray-700">
              {levelLabel}
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 text-2xl font-bold transition-colors"
        >
          ×
        </button>
      </div>

      {/* Team */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Team</h3>
        {user.team_name ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            {user.team_name}
          </span>
        ) : (
          <p className="text-sm text-gray-400 italic">No team</p>
        )}
      </div>

      {/* Reports To Section */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Reports To</h3>
        <div className="flex items-center gap-2 py-2">
          {manager ? (
            <>
              <span className="text-gray-900">{manager.name}</span>
              <span className="text-xs text-gray-400">({getLevelLabel(manager.level, levels)})</span>
            </>
          ) : (
            <span className="text-gray-500 italic">No manager (Top Level)</span>
          )}
        </div>
      </div>

      {/* Direct Reports Section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          Direct Reports ({directReports.length})
        </h3>
        {directReports.length > 0 ? (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {directReports.map(report => (
              <div
                key={report.id}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-gray-50 border-gray-200"
              >
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-gray-900">{report.name}</span>
                  <span className="ml-2 text-xs text-gray-400">{getLevelLabel(report.level, levels)}</span>
                  {report.team_name && (
                    <span className="ml-1 text-xs text-gray-400">· {report.team_name}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm italic py-2">No direct reports</p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AdminOrgStructure() {
  const [selectedUser, setSelectedUser] = useState<OrgUser | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchedUser, setSearchedUser] = useState<OrgUser | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Fetch users
  const { data: allUsers, isLoading } = useQuery({
    queryKey: ['admin', 'org-users'],
    queryFn: async () => {
      const response = await fetch(`${API_ORIGIN}/api/users`);
      const data = await response.json();
      return data.data as OrgUser[];
    },
  });

  // Fetch levels from DB
  const { data: levels } = useQuery({
    queryKey: ['admin', 'levels'],
    queryFn: getLevels,
  });

  // Filter to only show Jane Chen's hierarchy + Admin User
  const filteredUsers = useMemo(() => {
    if (!allUsers) return [];

    // Find Jane Chen (CEO)
    const janeChen = allUsers.find(u => u.name.toLowerCase().includes('jane chen'));

    // Find Admin User
    const adminUser = allUsers.find(u => u.name.toLowerCase() === 'admin user');

    if (!janeChen) {
      // If Jane Chen not found, show all users
      return allUsers;
    }

    // Get all descendants of Jane Chen
    const janeChenDescendants = getAllDescendantIds(allUsers, janeChen.id);

    // Filter to Jane Chen + descendants + Admin User
    return allUsers.filter(u =>
      u.id === janeChen.id ||
      janeChenDescendants.has(u.id) ||
      (adminUser && u.id === adminUser.id)
    );
  }, [allUsers]);

  // Build tree from filtered users
  const orgTree = useMemo(() => buildOrgTree(filteredUsers), [filteredUsers]);

  // Highlight path when searching
  const highlightPath = useMemo(() => {
    if (!searchedUser || !filteredUsers.length) return new Set<string>();

    const path = getPathToUser(filteredUsers, searchedUser.id);
    const pathSet = new Set(path);

    // Also include the searched user's direct reports
    filteredUsers
      .filter(u => u.manager_id === searchedUser.id)
      .forEach(u => pathSet.add(u.id));

    return pathSet;
  }, [searchedUser, filteredUsers]);

  // Auto-expand path when searching
  useEffect(() => {
    if (searchedUser && filteredUsers.length) {
      const path = getPathToUser(filteredUsers, searchedUser.id);
      setExpandedIds(new Set(path));
    }
  }, [searchedUser, filteredUsers]);

  // Handle search
  const handleSearch = () => {
    if (!searchTerm.trim() || !filteredUsers.length) {
      setSearchedUser(null);
      return;
    }

    const found = findUserByName(filteredUsers, searchTerm);
    setSearchedUser(found);

    if (found) {
      setSelectedUser(found);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchedUser(null);
    setExpandedIds(new Set());
  };

  // Get all descendant IDs for a user (to collapse entire subtree)
  const getDescendantIdsForCollapse = (userId: string): string[] => {
    const ids: string[] = [];
    const queue = [userId];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (currentId !== userId) ids.push(currentId);
      filteredUsers.forEach(u => {
        if (u.manager_id === currentId) {
          queue.push(u.id);
        }
      });
    }
    return ids;
  };

  const handleToggleExpand = (id: string, siblingIds: string[]) => {
    setExpandedIds(prev => {
      const next = new Set(prev);

      if (next.has(id)) {
        // Collapse: remove this node and all its descendants
        next.delete(id);
        getDescendantIdsForCollapse(id).forEach(descendantId => next.delete(descendantId));
      } else {
        // Expand: add this node, but first collapse siblings and their descendants
        siblingIds.forEach(siblingId => {
          if (siblingId !== id && next.has(siblingId)) {
            next.delete(siblingId);
            getDescendantIdsForCollapse(siblingId).forEach(descendantId => next.delete(descendantId));
          }
        });
        next.add(id);
      }

      return next;
    });
  };

  const expandAll = () => {
    setExpandedIds(new Set(filteredUsers.map(u => u.id)));
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  // Stats
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading organization structure...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Org Chart</h1>
          <p className="text-gray-500 mt-1">
            Visual hierarchy of reporting relationships
          </p>
        </div>
      </div>

      {/* Search and Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64 flex gap-2">
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              onClear={clearSearch}
              placeholder="Search for an employee..."
              className="flex-1"
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Search
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* Search Result */}
        {searchedUser && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <span className="text-yellow-800">
              Showing path to: <strong>{searchedUser.name}</strong> ({getLevelLabel(searchedUser.level, levels)})
            </span>
          </div>
        )}

        {searchTerm && !searchedUser && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-red-800">No employee found matching "{searchTerm}"</span>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex gap-6">
        {/* Org Chart */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 p-8 overflow-x-auto">
          {orgTree.length > 0 ? (
            <div className="flex justify-center min-w-max">
              <div className="flex flex-col items-center gap-4">
                {orgTree.map((node) => (
                  <VerticalTreeNode
                    key={node.user.id}
                    node={node}
                    levels={levels}
                    isHighlighted={!searchedUser || highlightPath.has(node.user.id)}
                    highlightPath={highlightPath}
                    expandedIds={orgTree.length === 1 ? new Set([...expandedIds, node.user.id]) : expandedIds}
                    onToggleExpand={handleToggleExpand}
                    onSelect={setSelectedUser}
                    selectedId={selectedUser?.id || null}
                    searchedUserId={searchedUser?.id || null}
                    siblingIds={orgTree.map(n => n.user.id)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No employees found in the organization
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedUser && filteredUsers && (
          <div className="w-96 flex-shrink-0 sticky top-8 self-start">
            <UserDetailPanel
              user={selectedUser}
              allUsers={filteredUsers}
              levels={levels}
              onClose={() => setSelectedUser(null)}
            />
          </div>
        )}
      </div>

    </div>
  );
}
